import React, { useState, useEffect } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import { CustomPortion } from '../types';
import { formatCarbs } from '../utils/carbCalculations';
import { calculateFPU } from '../utils/fpuCalculations';
import './AddCustomFoodModal.css';

interface AddCustomFoodModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingFoodId?: string; // If provided, we're editing an existing food
}

export const AddCustomFoodModal: React.FC<AddCustomFoodModalProps> = ({ 
  isOpen, 
  onClose,
  editingFoodId
}) => {
  const { preferences, addCustomFood, editCustomFood, getCustomFood } = usePreferences();
  const [formData, setFormData] = useState({
    name: '',
    carbs: '',
    fat: '',
    protein: ''
  });
  const [portions, setPortions] = useState<CustomPortion[]>([]);
  const [newPortion, setNewPortion] = useState({ name: '', grams: '' });
  const [errors, setErrors] = useState<string[]>([]);

  const isEditing = !!editingFoodId;

  const isGerman = preferences.language === 'de';

  // Load existing food data when editing
  useEffect(() => {
    if (isEditing && editingFoodId) {
      const existingFood = getCustomFood(editingFoodId);
      if (existingFood) {
        setFormData({
          name: existingFood.name,
          carbs: existingFood.carbs.toString(),
          fat: existingFood.fat.toString(),
          protein: existingFood.protein.toString()
        });
        setPortions(existingFood.portions || []);
      }
    } else {
      // Reset form for new food
      setFormData({ name: '', carbs: '', fat: '', protein: '' });
      setPortions([]);
    }
  }, [isEditing, editingFoodId, getCustomFood]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: string[] = [];
    
    // Validate required fields
    if (!formData.name.trim()) {
      newErrors.push(isGerman ? 'Name ist erforderlich' : 'Name is required');
    }
    
    if (!formData.carbs.trim()) {
      newErrors.push(isGerman ? 'Kohlenhydrate sind erforderlich' : 'Carbohydrates are required');
    } else {
      const carbsValue = parseFloat(formData.carbs);
      if (isNaN(carbsValue) || carbsValue < 0) {
        newErrors.push(isGerman ? 'Kohlenhydrate müssen eine gültige Zahl ≥ 0 sein' : 'Carbohydrates must be a valid number ≥ 0');
      }
    }
    
    // Validate optional fields if provided
    if (formData.fat.trim()) {
      const fatValue = parseFloat(formData.fat);
      if (isNaN(fatValue) || fatValue < 0) {
        newErrors.push(isGerman ? 'Fett muss eine gültige Zahl ≥ 0 sein' : 'Fat must be a valid number ≥ 0');
      }
    }
    
    if (formData.protein.trim()) {
      const proteinValue = parseFloat(formData.protein);
      if (isNaN(proteinValue) || proteinValue < 0) {
        newErrors.push(isGerman ? 'Eiweiß muss eine gültige Zahl ≥ 0 sein' : 'Protein must be a valid number ≥ 0');
      }
    }
    
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Add or edit the custom food
    const foodData = {
      name: formData.name.trim(),
      carbs: parseFloat(formData.carbs),
      fat: formData.fat.trim() ? parseFloat(formData.fat) : 0,
      protein: formData.protein.trim() ? parseFloat(formData.protein) : 0,
      portions: portions
    };

    if (isEditing && editingFoodId) {
      editCustomFood(editingFoodId, foodData);
    } else {
      addCustomFood(foodData);
    }
    
    // Reset form and close
    setFormData({ name: '', carbs: '', fat: '', protein: '' });
    setPortions([]);
    setNewPortion({ name: '', grams: '' });
    setErrors([]);
    onClose();
  };

  const handleClose = () => {
    setFormData({ name: '', carbs: '', fat: '', protein: '' });
    setPortions([]);
    setNewPortion({ name: '', grams: '' });
    setErrors([]);
    onClose();
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const addPortion = () => {
    const name = newPortion.name.trim();
    const grams = parseFloat(newPortion.grams);
    
    if (name && !isNaN(grams) && grams > 0) {
      setPortions(prev => [...prev, { name, grams }]);
      setNewPortion({ name: '', grams: '' });
    }
  };

  const removePortion = (index: number) => {
    setPortions(prev => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className="custom-food-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing 
            ? (isGerman ? 'Eigenes Lebensmittel bearbeiten' : 'Edit Custom Food')
            : (isGerman ? 'Eigenes Lebensmittel hinzufügen' : 'Add Custom Food')
          }</h2>
          <button className="modal-close-button" onClick={handleClose}>
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="custom-food-form">
          {errors.length > 0 && (
            <div className="form-errors">
              {errors.map((error, index) => (
                <div key={index} className="form-error">{error}</div>
              ))}
            </div>
          )}
          
          <div className="form-group inline">
            <label htmlFor="food-name" className="form-label">
              {isGerman ? 'Name' : 'Name'} *
            </label>
            <input
              id="food-name"
              type="text"
              className="form-input name-input"
              placeholder={isGerman ? 'z.B. Mein Lieblingsgericht' : 'e.g. My favorite dish'}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>
          
          <div className="form-group inline nutrient">
            <div className="nutrient-label-container">
              <label htmlFor="food-carbs" className="form-label">
                {isGerman ? 'Kohlenhydrate' : 'Carbs'} *
              </label>
              <span className="input-unit">(g/100g)</span>
            </div>
            <input
              id="food-carbs"
              type="number"
              step="0.1"
              min="0"
              className="form-input nutrient-input"
              placeholder="25.5"
              value={formData.carbs}
              onChange={(e) => handleInputChange('carbs', e.target.value)}
            />
          </div>
          
          <div className="form-group inline nutrient">
            <div className="nutrient-label-container">
              <label htmlFor="food-fat" className="form-label">
                {isGerman ? 'Fett' : 'Fat'}
              </label>
              <span className="input-unit">(g/100g)</span>
            </div>
            <input
              id="food-fat"
              type="number"
              step="0.1"
              min="0"
              className="form-input nutrient-input"
              placeholder="10.2"
              value={formData.fat}
              onChange={(e) => handleInputChange('fat', e.target.value)}
            />
          </div>
          
          <div className="form-group inline nutrient">
            <div className="nutrient-label-container">
              <label htmlFor="food-protein" className="form-label">
                {isGerman ? 'Eiweiß' : 'Protein'}
              </label>
              <span className="input-unit">(g/100g)</span>
            </div>
            <input
              id="food-protein"
              type="number"
              step="0.1"
              min="0"
              className="form-input nutrient-input"
              placeholder="15.8"
              value={formData.protein}
              onChange={(e) => handleInputChange('protein', e.target.value)}
            />
          </div>
          
          {/* Nutritional Preview for 100g */}
          {(formData.carbs || formData.fat || formData.protein) && (
            <div className="nutrition-preview">
              <h4 className="preview-title">
                {isGerman ? 'Nährwerte pro 100g:' : 'Nutrition per 100g:'}
              </h4>
              <div className="preview-nutrients">
                {formData.carbs && (
                  <span className="preview-carbs">
                    {formatCarbs(parseFloat(formData.carbs) || 0, preferences.carbUnit, preferences.language)}
                  </span>
                )}
                {preferences.showFPU && (formData.fat || formData.protein) && (
                  <span className="preview-fpu">
                    {calculateFPU(parseFloat(formData.fat) || 0, parseFloat(formData.protein) || 0).totalFPU.toFixed(1)} {isGerman ? 'FPE' : 'FPU'}
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Portions Section */}
          <div className="portions-section">
            <h3 className="portions-title">
              {isGerman ? 'Portionen (optional)' : 'Servings (optional)'}
            </h3>
            <p className="portions-description">
              {isGerman 
                ? 'Füge benutzerdefinierte Portionsgrößen hinzu. 100g wird automatisch als Standard angezeigt.'
                : 'Add custom serving sizes. 100g will automatically be shown as default.'
              }
            </p>
            
            {/* Add Portion */}
            <div className="add-portion">
              <div className="portion-inputs">
                <input
                  type="text"
                  className="portion-name-input"
                  placeholder={isGerman ? 'Portionsname (z.B. 1 Scheibe)' : 'Serving name (e.g. 1 slice)'}
                  value={newPortion.name}
                  onChange={(e) => setNewPortion(prev => ({ ...prev, name: e.target.value }))}
                />
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  className="portion-grams-input"
                  placeholder={isGerman ? 'Gramm' : 'Grams'}
                  value={newPortion.grams}
                  onChange={(e) => setNewPortion(prev => ({ ...prev, grams: e.target.value }))}
                />
                <button type="button" className="add-portion-button" onClick={addPortion}>
                  {isGerman ? 'Hinzufügen' : 'Add'}
                </button>
              </div>
            </div>
            
            {/* Portions List */}
            {portions.length > 0 && (
              <div className="portions-list">
                {portions.map((portion, index) => {
                  // Calculate nutritional values for this portion
                  const carbsPer100g = parseFloat(formData.carbs) || 0;
                  const fatPer100g = parseFloat(formData.fat) || 0;
                  const proteinPer100g = parseFloat(formData.protein) || 0;
                  
                  const carbsForPortion = (carbsPer100g * portion.grams) / 100;
                  const fatForPortion = (fatPer100g * portion.grams) / 100;
                  const proteinForPortion = (proteinPer100g * portion.grams) / 100;
                  
                  const fpuResult = calculateFPU(fatForPortion, proteinForPortion);
                  
                  return (
                    <div key={index} className="portion-item">
                      <div className="portion-info">
                        <div className="portion-details">
                          <span className="portion-name">{portion.name}</span>
                          <span className="portion-grams">({portion.grams}g)</span>
                          <span className="portion-carbs">
                            {formatCarbs(carbsForPortion, preferences.carbUnit, preferences.language)}
                          </span>
                          {preferences.showFPU && (
                            <span className="portion-fpu">
                              {fpuResult.totalFPU.toFixed(1)} {isGerman ? 'FPE' : 'FPU'}
                            </span>
                          )}
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="remove-portion-button"
                        onClick={() => removePortion(index)}
                        title={isGerman ? 'Portion entfernen' : 'Remove serving'}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleClose}>
              {isGerman ? 'Abbrechen' : 'Cancel'}
            </button>
            <button type="submit" className="submit-button">
              {isEditing 
                ? (isGerman ? 'Speichern' : 'Save')
                : (isGerman ? 'Hinzufügen' : 'Add Food')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};